import { z } from "zod";

export type Tab = "deposit" | "withdraw";

export const schema = z.object({
  chain: z.string().min(1, "Chain is required"),
  asset: z.string().min(1, "Asset is required"),
  address: z.string(),
  amount: z.coerce.number(),
});

export type CreateTransferFormValues = {
  chain: string;
  asset: string;
  address: string;
  amount: string;
};

export const CREATE_TRANSFER_DEFAULTS: CreateTransferFormValues = {
  chain: "",
  asset: "",
  address: "",
  amount: "",
};

type Values = z.output<typeof schema>;

export const refineCreateTransfer =
  ({
    tab,
    supportedAssetsByChain,
    whitelistAddresses,
    withdrawableAmount,
    feeAmount,
  }: {
    tab: Tab;
    supportedAssetsByChain: Record<string, Set<string>>;
    whitelistAddresses: string[];
    withdrawableAmount: number;
    feeAmount: number | null;
  }) =>
  (values: Values, refinementContext: z.RefinementCtx) => {
    const assetsForChain = values.chain
      ? supportedAssetsByChain[values.chain]
      : undefined;

    if (values.chain && !assetsForChain) {
      refinementContext.addIssue({
        code: "custom",
        path: ["chain"],
        message: "Unsupported chain",
      });
    } else if (values.asset && !assetsForChain?.has(values.asset)) {
      refinementContext.addIssue({
        code: "custom",
        path: ["asset"],
        message: "Unsupported asset for selected chain",
      });
    }

    if (tab !== "withdraw") {
      return;
    }
    // Withdraw Validation
    if (!values.address) {
      refinementContext.addIssue({
        code: "custom",
        path: ["address"],
        message: "Withdrawal address is required",
      });
    } else if (!whitelistAddresses.includes(values.address)) {
      refinementContext.addIssue({
        code: "custom",
        path: ["address"],
        message: "Address not in whitelist",
      });
    }
    if (!(values.amount > 0)) {
      refinementContext.addIssue({
        code: "custom",
        path: ["amount"],
        message: "Amount must be greater than 0",
      });
    } else {
      // Mirror the Max button: the most a user can withdraw is balance − fee.
      // When the fee can't be quoted, fall back to the raw balance so the form
      // never blocks a withdrawal on a missing fee.
      const maxAmount =
        feeAmount !== null
          ? Math.max(0, withdrawableAmount - feeAmount)
          : withdrawableAmount;
      if (values.amount > maxAmount) {
        refinementContext.addIssue({
          code: "custom",
          path: ["amount"],
          message:
            feeAmount !== null
              ? "Amount exceeds available balance after fees"
              : "Amount exceeds available balance",
        });
      }
    }
  };
